import React from 'react';
import {
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../../utils/formatters';
import type { Info360Documento } from '../types';
import { resolverAccionDocumento } from '../documentoAcciones';

interface Props {
  titulo: string;
  documentos: Info360Documento[];
  emptyText?: string;
  onAbrirDocumento?: (documento: Info360Documento) => void;
}

const Info360DocumentoList: React.FC<Props> = ({
  titulo,
  documentos,
  emptyText = 'Sin movimientos para mostrar.',
  onAbrirDocumento,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const items = documentos.slice(0, 5);

  const renderIdentificacion = (doc: Info360Documento) => {
    const accion = onAbrirDocumento ? resolverAccionDocumento(doc) : null;
    const texto = `${doc.tipo} ${doc.numero}`.trim();

    if (!accion) {
      return <>{texto}</>;
    }

    return (
      <Tooltip title={accion.label}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => onAbrirDocumento?.(doc)}
          aria-label={`${accion.label} ${doc.numero}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          {texto}
          <OpenInNew sx={{ fontSize: 14 }} aria-hidden />
        </Link>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {titulo}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : isMobile ? (
        <Stack spacing={1}>
          {items.map((doc, index) => (
            <Paper
              key={`${doc.tipo}-${doc.numero}-${index}`}
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 2 }}
            >
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={600} component="div">
                    {renderIdentificacion(doc)}
                  </Typography>
                  <Chip size="small" label={doc.estado} variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(doc.fecha)}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(doc.importe)}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((doc, index) => (
                <TableRow key={`${doc.tipo}-${doc.numero}-${index}`}>
                  <TableCell>{renderIdentificacion(doc)}</TableCell>
                  <TableCell>{formatDate(doc.fecha)}</TableCell>
                  <TableCell align="right">{formatCurrency(doc.importe)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={doc.estado} variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Info360DocumentoList;
